import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function ProcurementDetailModal({
  isOpen,
  onClose,
  data,
  onUpdateStatus,
  loading,
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (data && data.items) {
      setItems(data.items);
    }
  }, [data]);

  if (!isOpen || !data) return null;

  const handleUpdateStatus = (status) => {
    if (onUpdateStatus) {
      onUpdateStatus(status);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-30 bg-gray-500 bg-opacity-75 modal-backdrop-enter"></div>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pointer-events-none">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl sm:my-8 sm:w-full sm:max-w-[1100px] pointer-events-auto modal-panel-enter">
          {/* Header */}
          <div className="px-6 py-6 border-b border-gray-200 flex justify-between items-start">
            <div>
              <h3
                className="text-xl font-semibold leading-6 text-gray-900"
                id="modal-title">
                Purchase Order
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Buat permintaan pengadaan atau pembelian produk ke supplier
              </p>
            </div>
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* Supplier Info */}
            <div className="mb-6 flex justify-between items-center">
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded px-4 py-2 min-w-[260px]">
                <span className="text-sm font-medium text-gray-700">
                  {data.supplier?.name || "-"}
                </span>
              </div>

              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed"
                disabled>
                Tambah Produk
              </button>
            </div>

            {/* Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      No
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produk
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Unit
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Merek
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Jumlah
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Satuan
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-4 text-center text-sm text-gray-500">
                        Tidak ada item
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.product_name || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.unit_name || item.asset_unit_name || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.brand_name || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                          {item.qty_ordered ?? item.quantity ?? 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.product_unit_name || item.satuan_name || "Pcs"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleUpdateStatus("CANCELLED")}
                disabled={
                  loading ||
                  data.status === "CANCELLED" ||
                  data.status === "COMPLETED" ||
                  data.status === "ORDERED"
                }>
                Batalkan
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleUpdateStatus("ORDERED")}
                disabled={
                  loading ||
                  data.status === "ORDERED" ||
                  data.status === "CANCELLED" ||
                  data.status === "COMPLETED"
                }>
                Pesan Ke Supplier
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
